import { ref, computed, watch } from 'vue'
import { useProjectStore } from '@/stores/project'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { API, HOST } from '@/utils/http'

// 'Vlad Simankov' -> 'vlad_simankov'. Mirrors the backend slugify so
// client-derived handles (agents, stale member rows) match server ones.
export function slugify(value) {
  if (!value) return ''
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
}

// Mention candidates shared by every composer host (Chat tab footer, on-map
// compose bubble): project agents, then real project/org members. The
// `enabled` ref lets always-mounted hosts fetch lazily on first open.
export function useMentionCandidates(projectId, enabled = ref(true)) {
  const projectStore = useProjectStore()
  const orgStore = useOrgStore()
  const userStore = useUserStore()
  const projectAgents = ref([])
  let fetchedFor = null

  watch(
    [projectId, enabled],
    async ([id, on]) => {
      if (!id || !on || fetchedFor === id) return
      fetchedFor = id
      projectStore.fetchMembers(id)
      const orgId = orgStore.selectedOrg
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project-agents/${id}`,
        })
        // A slower earlier project must not overwrite the one now open.
        if (fetchedFor !== id) return
        projectAgents.value = response.data
      } catch (e) {
        if (fetchedFor !== id) return
        projectAgents.value = []
      }
    },
    { immediate: true }
  )

  const me = computed(() => userStore.member)

  // Agents are mapped into the same candidate shape the mention picker already
  // consumes, with a namespaced string id that cannot collide with a numeric
  // member_id.
  const agentCandidates = computed(() =>
    projectAgents.value
      .filter((pa) => pa.enabled)
      .map((pa) => ({
        member_id: `agent:${pa.agent.id}`,
        display_name: pa.agent.name,
        nickname: slugify(pa.agent.name),
        avatar_url: null,
        emoji: pa.agent.emoji,
        role: 'Agent',
        agent_id: pa.agent.id,
      }))
  )

  const mentionCandidates = computed(() => {
    const all = [
      ...agentCandidates.value,
      ...(projectStore.members || []),
      ...(orgStore.currentMembers || []),
    ]
    const seen = new Set()
    const unique = all.filter((m) => {
      if (!m.member_id || m.member_id === me.value?.member_id || seen.has(m.member_id)) return false
      seen.add(m.member_id)
      return true
    })
    // Every candidate needs a distinct handle: identity stays member_id/agent_id,
    // the nickname is only the text token inserted into the message.
    const taken = new Set()
    return unique.map((m) => {
      const base = m.nickname || slugify(m.display_name) || `member${m.member_id}`
      let nickname = base
      for (let suffix = 2; taken.has(nickname); suffix += 1) nickname = `${base}${suffix}`
      taken.add(nickname)
      return { ...m, nickname }
    })
  })

  return { mentionCandidates }
}
